import React, { useState, useEffect, useMemo, useCallback } from "react";
import _ from "lodash";
import ThemeableAgGrid from "../../../common/components/agGrid/ThemeableAgGrid";
import Loading from "../../../common/components/loading/loading";
import { getVenueHistory } from "../../../common/services/markets/venueService";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";

const VenueHistory = ({ venueId }) => {
  const { t } = useTranslation(["markets"]);
  const [venueHistory, setVenueHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [paginationState, setPaginationState] = useState({
    currentPage: 0,
    pageSize: 10,
  });

  // Fetch venue history on component mount
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setIsLoading(true);
        const data = await getVenueHistory(venueId);
        const formattedData = mapToViewModel(data);
        setVenueHistory(_.orderBy(formattedData, "id", "desc"));
      } catch (error) {
        console.error("Failed to fetch venue history:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueId]);

  // Map API data to view model with formatted dates
  const mapToViewModel = useCallback((venues) => {
    const dateFormatOptions = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    };

    return venues.map((venue) => ({
      id: venue.id,
      name: venue.name,
      description: venue.description,
      updatedByUserAccountId: venue.createdByUserAccountId,
      updatedBy:
        venue.updatedByUserAccountFirstName +
        " " +
        venue.updatedByUserAccountLastName,
      updatedDate: new Date(venue.updatedDate).toLocaleDateString(
        "en-US",
        dateFormatOptions
      ),
      writeDate: new Date(venue.writeDate).toLocaleDateString(
        "en-US",
        dateFormatOptions
      ),
      eventId: venue.eventId,
    }));
  }, []);

  // Memoize column definitions
  const columnDefs = useMemo(
    () => [
      {
        field: "id",
        headerName: t("RecordID"),
        sortable: true,
        filter: true,
        sort: "desc",
        minWidth: 120,
        flex: 0.8,
      },
      {
        field: "name",
        headerName: t("VenueName"),
        sortable: true,
        filter: true,
        minWidth: 150,
        flex: 1.2,
        tooltipValueGetter: (params) => params.value,
      },
      {
        field: "description",
        headerName: t("VenueDescription"),
        sortable: true,
        filter: true,
        minWidth: 200,
        flex: 1.5,
        tooltipValueGetter: (params) => params.value,
      },
      {
        field: "active",
        headerName: t("Active"),
        flex: 1,
        // Apply centered class for better alignment
        cellClass: "ag-center-aligned-cell",
        headerClass: "ag-center-aligned-header",
        filterValueGetter: (params) => params.data.activeString,
        cellRenderer: (params) => {
          const isActive = params.data?.active;
          return (
            <div className="centered" style={{ width: "100%", height: "100%" }}>
              <FontAwesomeIcon
                icon={isActive ? faCheck : faTimes}
                className={isActive ? "text-success" : "text-danger"}
                style={{ fontSize: "16px" }}
              />
            </div>
          );
        },
      },
      {
        field: "updatedBy",
        headerName: t("UpdatedBy"),
        sortable: true,
        filter: true,
        minWidth: 150,
        flex: 1.2,
      },
      {
        field: "updatedDate",
        headerName: t("UpdateDate"),
        sortable: true,
        filter: true,
        minWidth: 180,
        flex: 1.2,
      },
      {
        field: "writeDate",
        headerName: t("WriteDate"),
        sortable: true,
        filter: true,
        minWidth: 180,
        flex: 1.2,
      },
      {
        field: "eventId",
        headerName: t("EventID"),
        sortable: true,
        filter: true,
        minWidth: 120,
        flex: 0.8,
      },
    ],
    [t]
  );

  // Default column definition
  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      minWidth: 100,
      sortable: true,
      filter: true,
    }),
    []
  );

  // Pagination configuration
  const paginationConfig = useMemo(
    () => ({
      enabled: true,
      pageSize: paginationState?.pageSize || 10,
      pageSizeOptions: [10, 20, 50, 100],
    }),
    [paginationState?.pageSize]
  );

  // Handle pagination changes
  const handlePaginationChanged = useCallback((params) => {
    if (params && params.api) {
      setPaginationState({
        currentPage: params.api.paginationGetCurrentPage(),
        pageSize: params.api.paginationGetPageSize(),
      });
    }
  }, []);

  // Grid ready handler
  const onGridReady = useCallback(
    (params) => {
      if (paginationState && paginationState.currentPage >= 0) {
        setTimeout(() => {
          params.api.paginationGoToPage(paginationState.currentPage);
        }, 0);
      }
    },
    [paginationState]
  );

  if (isLoading) return <Loading />;

  return (
    <div className="history-table-container">
      <div className="mb-3">
        {t("VenueID")} {venueId}
      </div>
      <div style={{ height: "600px", width: "100%" }}>
        <ThemeableAgGrid
          className="table-extra-small"
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowData={venueHistory}
          getRowId={(params) => params.data.id.toString()}
          animateRows={true}
          onGridReady={onGridReady}
          rowHeight={40}
          domLayout="autoHeight"
          pagination={true}
          paginationPageSize={paginationConfig.pageSize}
          paginationPageSizeSelector={paginationConfig.pageSizeOptions}
          suppressPaginationPanel={false}
          onPaginationChanged={handlePaginationChanged}
        />
      </div>
    </div>
  );
};

export default VenueHistory;

